import api from './axiosInstance';

const BASE = '/legal-documents';

const subir = async (ruta, documentos, caseId, signal, extra = {}) => {
  const formData = new FormData();
  (documentos || []).forEach((archivo) => formData.append('documentos', archivo));
  if (caseId) formData.append('caseId', caseId);

  Object.entries(extra).forEach(([clave, valor]) => {
    if (valor !== undefined && valor !== null) formData.append(clave, valor);
  });

  const response = await api.post(ruta, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response.data;
};

// ─── Excepciones (art. 464 CPC) ──────────────────────────────────────────────

/**
 * Analiza el expediente y propone las defensas del art. 464.
 *
 * El modelo informa HECHOS por cada excepción —si el presupuesto consta, en qué se apoya, qué
 * documentos necesita— y el veredicto lo calcula el backend. Antes era un juicio del modelo y el
 * mismo expediente daba resultados distintos entre corridas.
 */
/**
 * @param ausenciasSonOmisionDelEjecutante lo marca el abogado y cambia el sentido del análisis:
 *   marcado, un documento que falte funda las excepciones como omisión de la contraria
 *   (art. 1698 CC); sin marcar, se declara como dato faltante. El código no lo puede deducir.
 */
export const analizarExcepciones = (
  documentos,
  caseId,
  signal,
  ausenciasSonOmisionDelEjecutante = false
) =>
  subir(`${BASE}/excepciones/analizar`, documentos, caseId, signal, {
    ausenciasSonOmisionDelEjecutante,
  });

/** Recalcula el veredicto sobre las excepciones que el abogado corrigió. */
export const recalcularExcepciones = async (analisis, signal) => {
  const response = await api.post(`${BASE}/excepciones/recalcular`, analisis, { signal });
  return response.data;
};

/**
 * Genera el escrito con las excepciones seleccionadas. Devuelve el texto, el .docx en base64 y
 * las advertencias que hay que leer antes de presentarlo.
 */
export const generarEscritoExcepciones = async (
  analisis,
  codigos,
  comparecencia,
  caseId,
  signal
) => {
  const response = await api.post(
    `${BASE}/excepciones/generar`,
    { analisis, codigos, comparecencia, caseId },
    { signal }
  );
  return response.data;
};

// ─── Apelación ───────────────────────────────────────────────────────────────

/**
 * Lee la sentencia y busca los agravios que se le imputan al fallo.
 * El plazo para apelar es fatal y lo computa el backend en días hábiles.
 */
export const analizarApelacion = (documentos, caseId, signal) =>
  subir(`${BASE}/apelacion/analizar`, documentos, caseId, signal);

/**
 * Genera el recurso con las excepciones rechazadas que el abogado seleccionó. El modelo redacta el
 * cuerpo; el código arma el documento con el formato institucional y revisa el estilo.
 */
export const generarEscritoApelacion = async (
  analisis,
  excepcionesIds,
  abogado,
  caseId,
  signal
) => {
  const response = await api.post(
    `${BASE}/apelacion/generar`,
    { analisis, excepcionesIds, abogado, caseId },
    { signal }
  );
  return response.data;
};

export const recalcularApelacion = async (analisis, signal) => {
  const response = await api.post(`${BASE}/apelacion/recalcular`, analisis, { signal });
  return response.data;
};

// ─── Plantillas (sin IA) ─────────────────────────────────────────────────────

/** Las plantillas aprobadas. Están disponibles en todas las áreas: no hay filtro por área. */
export const getPlantillas = async (typeId, signal) => {
  const response = await api.get(`${BASE}/plantillas`, {
    params: typeId ? { typeId } : undefined,
    signal,
  });
  return response.data || [];
};

/**
 * TODAS las plantillas, incluidas las pendientes y las dadas de baja, con su estado, cuántos
 * campos tienen y cuántos escritos se generaron con cada una. Es la vista de administración;
 * `getPlantillas` es la del selector y solo trae las aprobadas y activas.
 */
export const getPlantillasAdministrables = async (signal) => {
  const response = await api.get(`${BASE}/plantillas/administrar`, { signal });
  return response.data || [];
};

/** Aprueba o rechaza una plantilla. El rechazo exige motivo: sin él el backend devuelve 400. */
export const decidePlantilla = async (plantillaId, aprobar, motivo, signal) => {
  const response = await api.post(
    `${BASE}/plantillas/${plantillaId}/decidir`,
    { aprobar, motivo },
    { signal }
  );
  return response.data;
};

/** Activa o desactiva una plantilla aprobada. No la borra: conserva su historial. */
export const updatePlantillaVigencia = async (plantillaId, activa, signal) => {
  const response = await api.post(
    `${BASE}/plantillas/${plantillaId}/vigencia`,
    { activa },
    { signal }
  );
  return response.data;
};

/**
 * Sube un .docx como plantilla nueva. El backend le detecta los {{MARCADOR}} y arma los campos.
 * Entra como PENDING: no aparece en el selector hasta que alguien la apruebe.
 */
export const uploadPlantillaDocx = async (datos, signal) => {
  const formData = new FormData();
  formData.append('archivo', datos.archivo);
  if (datos.nombre) formData.append('nombre', datos.nombre);
  if (datos.descripcion) formData.append('descripcion', datos.descripcion);
  if (datos.categoria) formData.append('categoria', datos.categoria);
  if (datos.grupo) formData.append('grupo', datos.grupo);
  if (datos.typeId) formData.append('typeId', datos.typeId);

  const response = await api.post(`${BASE}/plantillas`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response.data;
};

/**
 * Los campos de la plantilla. Con `caseId`, los que salen de la causa vienen YA RELLENOS y
 * marcados con `autocompletado`: el formulario es el mismo que el abogado conoce, pero llega con
 * la mayor parte hecha. Todos siguen siendo editables.
 */
export const getCamposPlantilla = async (plantillaId, caseId, signal) => {
  const response = await api.get(`${BASE}/plantillas/${plantillaId}/campos`, {
    params: caseId ? { caseId } : undefined,
    signal,
  });
  return response.data || [];
};

/**
 * Genera el escrito desde plantilla.
 *
 * Ojo con `marcadoresSinResolver` en la respuesta: si viene con algo, el documento quedó con
 * marcadores literales y NO está listo para presentar.
 */
export const generarDesdePlantilla = async (caseId, plantillaId, valores, signal) => {
  const response = await api.post(
    `${BASE}/plantillas/generar`,
    { caseId, plantillaId, valores },
    { signal }
  );
  return response.data;
};
